import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { useLocale } from "@/context/LocaleContext";
import { AuthPageHeader } from "@/components/AuthPageHeader";
import { translate } from "@/lib/uiStrings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

function formatResetPasswordError(err, lang) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  const st = err?.response?.status;
  if (!err?.response && err?.message === "Network Error") {
    return translate(lang, "auth.errNetwork");
  }
  if (st) return translate(lang, "auth.errServer", { status: String(st) });
  if (err?.message) return err.message;
  return translate(lang, "auth.errGeneric");
}

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token")?.trim() || "", [searchParams]);
  const { resetPassword } = useAuth();
  const { lang, t } = useLocale();
  const nav = useNavigate();

  const fieldClass =
    "mt-1 h-10 rounded-lg border-[var(--line-2)] bg-[var(--bg)] shadow-none focus-visible:ring-1 focus-visible:ring-[var(--line)] focus-visible:border-[var(--line)]";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("resetPassword.invalidLink"));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("resetPassword.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success(t("resetPassword.success"));
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatResetPasswordError(err, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col bg-[var(--bg)] overflow-x-hidden">
      <AuthPageHeader />
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-8">
        <div className="w-full max-w-[min(400px,100%)] rounded-2xl border border-[var(--line-2)] bg-[var(--panel-2)] shadow-sm p-5 sm:p-6">
          <header className="mb-4">
            <p className="text-sm font-medium text-[var(--text)] leading-snug">
              {t("resetPassword.title")}
            </p>
            <p className="mt-1 text-[var(--text-dim)] text-sm leading-snug">
              {token ? t("resetPassword.subtitle") : t("resetPassword.invalidLink")}
            </p>
          </header>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-[var(--text-dim)]">
                {t("resetPassword.newPassword")}
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={!token}
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={!token}
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  className="absolute right-0 top-0 inline-flex h-full w-11 items-center justify-center text-[var(--text-dim)] transition-colors hover:text-[var(--text)] focus:outline-none focus-visible:text-[var(--text)] disabled:pointer-events-none disabled:opacity-45"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-[var(--text-dim)]">
                {t("resetPassword.confirmPassword")}
              </Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={!token}
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  disabled={!token}
                  aria-label={showConfirmPassword ? t("login.hidePassword") : t("login.showPassword")}
                  className="absolute right-0 top-0 inline-flex h-full w-11 items-center justify-center text-[var(--text-dim)] transition-colors hover:text-[var(--text)] focus:outline-none focus-visible:text-[var(--text)] disabled:pointer-events-none disabled:opacity-45"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={busy || !token}
              className="w-full h-10 rounded-lg bg-[var(--text)] text-[var(--bg)] text-sm font-semibold tracking-wide hover:opacity-90 transition-opacity disabled:opacity-45 disabled:pointer-events-none"
            >
              {busy ? t("resetPassword.submitBusy") : t("resetPassword.submit")}
            </button>
          </form>
        </div>
        <Link
          to="/login"
          className="mt-5 text-sm text-[var(--text-dim)] transition-colors hover:text-[var(--text)]"
        >
          {t("resetPassword.backToLogin")}
        </Link>
      </div>
    </div>
  );
}
